import React, { useCallback, useEffect, useState } from 'react';
import { TrendingUp, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { toast } from 'sonner';
import { apiCall } from '../../utils/api';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/** Phase 4 Wave C — statistical forecast chart + Claude AI narrative. */
export default function FinanceForecastPanel() {
  const [forecast, setForecast] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(() => {
    apiCall('/api/admin/finance/intelligence/forecast')
      .then((d) => setForecast(d.forecast))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runAi = async (force = false) => {
    setAiLoading(true);
    try {
      const d = await apiCall('/api/admin/finance/intelligence/ai-insights', {
        method: 'POST',
        body: JSON.stringify({ force }),
      });
      setAi(d);
    } catch (e) {
      toast.error(e?.message || 'AI insights failed');
    } finally { setAiLoading(false); }
  };

  if (!forecast) return null;

  const chartData = [
    ...forecast.history_weeks.map((w) => ({ week: w.week_start, actual: w.total })),
    ...forecast.forecast_weeks.map((w) => ({ week: w.week_start, projected: w.revenue })),
  ];
  const splitWeek = forecast.forecast_weeks[0]?.week_start;

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="finance-forecast-panel">
      <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-emerald-600" /> Revenue Forecast — next 4 weeks
          </h4>
          <span className="text-[11px] text-gray-400">{forecast.method}</span>
        </div>
        <div className="flex gap-4 mb-2 text-xs">
          <span className="text-gray-500">Projected 4-wk revenue: <b className="text-gray-800" data-testid="forecast-4wk-revenue">{money(forecast.projected_4wk_revenue)}</b></span>
          <span className="text-gray-500">Payroll volume: <b className="text-gray-800">{money(forecast.projected_4wk_payroll_volume)}</b></span>
          <span className="text-gray-500">MRR: <b className="text-gray-800">{money(forecast.projected_mrr)}</b></span>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={money0} width={54} />
            <Tooltip formatter={(v, n) => [money(v), n]} />
            {splitWeek && <ReferenceLine x={splitWeek} stroke="#cbd5e1" strokeDasharray="4 4" label={{ value: 'forecast →', fontSize: 10, fill: '#94a3b8' }} />}
            <Area type="monotone" dataKey="actual" name="Actual" stroke="#10b981" fill="#10b98122" strokeWidth={2} />
            <Line type="monotone" dataKey="projected" name="Projected" stroke="#6366f1" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="finance-ai-insights">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-violet-500" /> AI Insights
          </h4>
          <button
            onClick={() => runAi(!!ai)}
            disabled={aiLoading}
            className="text-xs px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="run-ai-insights-btn"
          >
            {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {ai ? 'Regenerate' : 'Generate'}
          </button>
        </div>
        {!ai ? (
          <p className="text-xs text-gray-400 py-6 text-center">Generate a CFO-level narrative of the forecast using Claude.</p>
        ) : (
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {(ai.insights || []).map((s, i) => (
              <p key={`i-${i}`} className="text-xs text-gray-700 flex gap-1.5"><span className="text-emerald-500 shrink-0">▲</span>{s}</p>
            ))}
            {(ai.risks || []).map((s, i) => (
              <p key={`r-${i}`} className="text-xs text-gray-700 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />{s}</p>
            ))}
            <p className="text-[10px] text-gray-400 pt-1">{ai.cached ? 'Cached · ' : ''}{ai.generated_at && new Date(ai.generated_at).toLocaleString()}</p>
          </div>
        )}
      </div>
    </div>
  );
}
